import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { useAppSelector } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import DocumentMeta from 'core/components/DocumentMeta'
import DateCell, { DateAndTime } from 'core/components/listTable/cells/DateCell'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import {
  createResourceLabelsCell,
  renderLabelsAsBadges,
  renderResourceLabels,
} from 'k8s/components/common/entity/labels-and-annotations/helpers'
import { K8sPluginGlobalPrefs } from 'k8s/model'
import { pick } from 'ramda'
import React, { useEffect, useState } from 'react'
import { renderPvcStatus } from '../../resources/persistent-volume-claims/helpers'
import AddPersistentVolumeModal from './AddPersistentVolumeModal'
import DeletePersistentVolumeDialog from './DeletePersistentVolumeDialog'
import { deletePersistentVolume, listPersistentVolumes } from './actions'
import { IPersistentVolumeDetailsPageTabs } from './model'
import { persistentVolumeSelector } from './selectors'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'

type ModelDataKey = DataKeys.PersistentVolumes
type SelectorModel = ArrayElement<ReturnType<typeof persistentVolumeSelector>>
type ActionParams = InferActionParams<typeof listPersistentVolumes>

type Params = ActionParams & {
  namespace?: string
  healthyClusters: boolean
}

const defaultParams: Params & SortingState = {
  clusterId: null,
  healthyClusters: true,
  orderBy: 'created',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'PersistentVolumes',
  listTablePrefs,
)

const searchTargets = ['name', 'clusterName']

const columns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    // width: 'medium',
    CellComponent: createGridLinkCell({
      routeToFn: ({ clusterName, id }) =>
        routeManager.routes.kubernetes.resources.persistentVolumes.details.path({
          cluster: clusterName,
          id,
          tab: IPersistentVolumeDetailsPageTabs.Overview,
        }),
    }),
  } as GridViewColumn<SelectorModel, 'name'>,
  // { key: 'namespace', label: 'Namespace' },
  { key: 'capacity', label: 'Capacity', render: renderResourceLabels({ type: 'table' }) },
  {
    key: 'accessModes',
    label: 'Access Modes',
    render: renderLabelsAsBadges({ type: 'table', variant: 'default' }),
  },
  { key: 'reclaimPolicy', label: 'Reclaim Policy' },
  { key: 'status.phase', label: 'Status', render: renderPvcStatus },
  { key: 'spec.claimRef.name', label: 'Claim' },
  {
    key: 'metadata.annotations',
    label: 'Annotations',
    CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
  },
  { key: 'storageClass', label: 'Storage Class' },
  {
    key: 'created',
    label: 'Created',
    render: (value: string) => <DateAndTime value={value} />,
  },
]

export default function PersistentVolumesListPage() {
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences<K8sPluginGlobalPrefs>(
    'k8sPluginGlobalParams',
  )
  const clusterId = k8sPluginGlobalPerfs?.clusterId || ''
  const clusterName = k8sPluginGlobalPerfs?.cluster || ''
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { message, loading, reload } = useListAction(listPersistentVolumes, {
    params: { clusterId },
  })
  const data = useAppSelector(persistentVolumeSelector)
  const [selectedPersistentVolume, setSelectedPersistentVolume] = useState<SelectorModel>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)

  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = [
    {
      // cond: (item) => canDelete([item]),
      label: 'Delete',
      icon: 'trash-alt',
      handleClick: (persistentVolume) => {
        setSelectedPersistentVolume(persistentVolume)
        setShowDeleteDialog(true)
      },
      refreshAfterSuccess: true,
      hideIfDisabled: true,
    },
  ]

  useEffect(() => {
    if (clusterId) {
      reload(true)
    }
  }, [clusterId])
  return (
    <>
      <DocumentMeta title="Persistent Volumes" />
      <AddPersistentVolumeModal data-testid="add-persistent-volume-modal" />
      {showDeleteDialog && (
        <DeletePersistentVolumeDialog
          onClose={() => setShowDeleteDialog(false)}
          rows={[selectedPersistentVolume]}
          data-testid="delete-persistent-volume-dialog"
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.PersistentVolumes}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        columns={columns}
        data={data}
        addUrl={routeManager.routes.kubernetes.resources.persistentVolumes.add.path({
          cluster: clusterName,
        })}
        addText="Add Persistent Volume"
        getParamsUpdater={getParamsUpdater}
        deleteAction={deletePersistentVolume}
        rowMenuItems={rowMenuItems}
        showRowMenuForSingleRowActions
        {...pick(listTablePrefs, params)}
        data-testid="persistent-volumes-list-container"
      />
    </>
  )
}

const toFilterColumns = {
  namespace: true,
  clusterName: true,
  labels: true,
  storageClass: true,
  created: true,
}
const formatOpts = {
  renameLabels: {},
  labels: ['d', 'h'],
  pluralize: false,
}
export const getPvDetailOptions = (pluginId = 'kubernetes') => {
  return {
    title: 'Persistent Volumes',
    columns: [
      ...columns.filter((column) => !toFilterColumns[column.key]),
      {
        key: 'created',
        label: 'Age',
        render: (value) => (
          <DateCell value={value} formatOpts={formatOpts} showToolTip difference />
        ),
      },
    ],
    columnsOrder: [
      'name',
      'created',
      'capacity',
      'accessModes',
      'reclaimPolicy',
      'spec.claimRef.name',
      'status.phase',
    ],
  }
}
